"use client";

import { c as _c } from "react/compiler-runtime";
import { formatLabel } from "@/utils/string";
import { memo, useId } from "react";
import { FieldDescription } from "./FieldDescription";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-javascript";
interface PropertySchema {
  title?: string;
  description?: string;
  required?: boolean;
  maxLength?: number;
  [key: string]: unknown;
}
interface CodeEditorInputProps {
  name: string;
  property: PropertySchema;
  value: string;
  onChange: (value: string) => void;
  allValues?: Record<string, unknown>;
  hideLabel?: boolean;
}
const CODE_PLACEHOLDER = `// \`input\` = whatever you set in the Input field below
// Return the transformed result

function transform(input) {
  return input;
}`;
function CodeEditorInput(t0) {
  const $ = _c(30);
  const {
    name,
    property,
    value: t1,
    onChange,
    allValues: t2,
    hideLabel: t3
  } = t0;
  const value = t1 === undefined ? "" : t1;
  t2 === undefined ? {} : t2;
  const hideLabel = t3 === undefined ? false : t3;
  const editorId = useId();
  const isOverLimit = property.maxLength && value.length > property.maxLength;
  const isEmpty = !value.trim();
  let t4;
  if ($[0] !== editorId || $[1] !== hideLabel || $[2] !== name || $[3] !== property.required || $[4] !== property.title) {
    t4 = !hideLabel && <label htmlFor={editorId} className="field-label block">{property.title || formatLabel(name)}{property.required && <span className="ml-1 text-red-500">*</span>}</label>;
    $[0] = editorId;
    $[1] = hideLabel;
    $[2] = name;
    $[3] = property.required;
    $[4] = property.title;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== editorId || $[7] !== property.description) {
    t5 = property.description && <FieldDescription id={`${editorId}-description`} text={property.description} />;
    $[6] = editorId;
    $[7] = property.description;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = !hideLabel ? editorId : undefined;
  const t7 = property.description ? `${editorId}-description` : undefined;
  let t8;
  if ($[9] !== isEmpty) {
    t8 = isEmpty && <div className="text-ds-text-disabled dark:text-ds-text-subtle text-2-5xs pointer-events-none absolute inset-0 p-3 font-mono leading-normal" aria-hidden="true"><pre style={{
        margin: 0
      }}>{CODE_PLACEHOLDER}</pre></div>;
    $[9] = isEmpty;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      fontFamily: "\"Fira Code\", \"Fira Mono\", Menlo, Monaco, \"Courier New\", monospace",
      fontSize: 13,
      minHeight: "200px"
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== editorId || $[13] !== onChange || $[14] !== value) {
    t10 = <Editor value={value} onValueChange={onChange} highlight={_temp} padding={12} style={t9} className="code-editor" preClassName="text-ds-text-primary dark:text-zinc-200" textareaClassName="focus:outline-none caret-zinc-900 dark:caret-white" textareaId={editorId} />;
    $[12] = editorId;
    $[13] = onChange;
    $[14] = value;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t11 = <div className="border-ds-neutral-300 dark:border-ds-neutral-700 relative overflow-hidden rounded-md border bg-white dark:bg-[#282c34]" role="group" aria-labelledby={t6} aria-describedby={t7}>{t8}{t10}</div>;
    $[16] = t10;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== isOverLimit || $[22] !== property.maxLength || $[23] !== value.length) {
    t12 = property.maxLength && <p className={`text-xs ${isOverLimit ? "font-medium text-red-600 dark:text-red-400" : "text-ds-text-muted"}`} role="status" aria-live="polite">{value.length} / {property.maxLength} characters{isOverLimit && <span className="ml-1">({value.length - property.maxLength} over limit)</span>}</p>;
    $[21] = isOverLimit;
    $[22] = property.maxLength;
    $[23] = value.length;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t4 || $[28] !== t5) {
    t13 = <div className="space-y-2">{t4}{t5}{t11}{t12}</div>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t4;
    $[28] = t5;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}

// Memoize component to prevent unnecessary re-renders
function _temp(code) {
  return highlight(code, languages.javascript, "javascript");
}
export default memo(CodeEditorInput, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value && prevProps.name === nextProps.name && prevProps.hideLabel === nextProps.hideLabel && prevProps.property.title === nextProps.property.title && prevProps.property.description === nextProps.property.description && prevProps.property.required === nextProps.property.required && prevProps.property.maxLength === nextProps.property.maxLength;
});